import Icon1 from "@assets/icons/Icon";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { StatusBlock } from "@components/ui/blocks/Status";
import { useDataStore } from "@store/user_org";
import { useProjectStore } from "@store/projectStore";
import { navigateTo } from "context/navigation.service";
import { getLocalFromFlutter } from "@services/timeHelpers";
import { EmailSentModal } from "@components/modals/EmailSentModal";
import { useEffect, useState } from "react";

function StatCard({
  label,
  value,
  icon,
  color,
  onClick,
}: {
  label: string;
  value: number;
  icon: string;
  color: string;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={`flex flex-col gap-1 bg-white border rounded-xl p-4 flex-1 min-w-140 shadow-lg ${onClick ? "cursor-pointer hover:shadow-xl transition-shadow" : ""}`}
    >
      <div className="flex flex-row items-center justify-between">
        <div className="text-h3 text-neutral-400">{label}</div>
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: color + "20" }}
        >
          <Icon1 name={icon} size={18} color={color} />
        </div>
      </div>
      <div className="text-2xl font-semibold text-black">{value}</div>
    </div>
  );
}

const statusConfig: Record<
  string,
  { label: string; type: "success" | "info" | "warning" | "error" | "default" }
> = {
  not_started: { label: "En attente", type: "default" },
  in_progress: { label: "En cours", type: "info" },
  delivered: { label: "Livré", type: "success" },
  sav: { label: "SAV", type: "warning" },
};

export default function HomeScreen() {
  const userData = useDataStore((state) => state.userData);
  const orgData = useDataStore((state) => state.orgData);
  const projects = useProjectStore((state) => state.projects);
  const broadcasts = useProjectStore((state) => state.broadcasts);
  const partners = useProjectStore((state) => state.partners);
  const sentEmails = useProjectStore((state) => state.sentEmails);
  const fetchProjects = useProjectStore((state) => state.fetchProjects);
  const fetchProject = useProjectStore((state) => state.fetchProject);
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<import("types").Project | undefined>();

  useEffect(() => {
    if (selectedEmailId) {
      const projectId = sentEmails.find((e) => e.id === selectedEmailId)?.project_id;
      if (projectId) fetchProject(projectId).then(setSelectedProject);
      else setSelectedProject(undefined);
    } else {
      setSelectedProject(undefined);
    }
  }, [selectedEmailId]);

  useEffect(() => {
    if (orgData?.id) {
      fetchProjects(orgData.id);
    }
  }, [orgData?.id]);

  const projectsByStatus = {
    not_started: projects.filter((p) => p.status === "not_started").length,
    in_progress: projects.filter((p) => p.status === "in_progress").length,
    delivered: projects.filter((p) => p.status === "delivered").length,
    sav: projects.filter((p) => p.status === "sav").length,
  };

  const activeBroadcasts = broadcasts.filter(
    (b) => b.status === "active",
  ).length;

  const recentProjects = [...projects]
    .sort(
      (a, b) =>
        b.infos.general.creation_date.seconds -
        a.infos.general.creation_date.seconds,
    )
    .slice(0, 5);

  const recentEmails = [...sentEmails]
    .sort((a, b) => b.created_at.seconds - a.created_at.seconds)
    .slice(0, 4);


  return (
    <div className="flex flex-col  overflow-auto max-h-screen">
      {selectedEmailId && (
        <EmailSentModal
          onClose={() => setSelectedEmailId(null)}
          email_sent_id={selectedEmailId}
          sentEmails={sentEmails}
          activeProject={selectedProject}
          orgData={orgData || undefined}
        />
      )}
      <div className="flex flex-col h-fit pb-96 mt-10 ml-10 mr-10">
        <div className="flex flex-col w-full max-w-1k gap-6">
          {/* Header */}
          <div className="flex flex-row w-full justify-between items-start">
            <div className="flex flex-col">
              <Text1>{`Bonjour${userData?.infos.display_name ? `, ${userData.infos.display_name}` : ""}`}</Text1>
              <Text2>Voici un apercu de votre activité.</Text2>
            </div>
          </div>

          {/* Stats */}
          <div className="flex flex-row gap-3 flex-wrap">
            <StatCard
              label="Projets"
              value={projects.length}
              icon="cube"
              color="#3B82F6"
              onClick={() => navigateTo({ route: "/projects" })}
            />
            <StatCard
              label="En cours"
              value={projectsByStatus.in_progress}
              icon="restart"
              color="#0EA5E9"
              onClick={() =>
                navigateTo({ route: "/projects?status=in_progress" })
              }
            />
            <StatCard
              label="Livrés"
              value={projectsByStatus.delivered}
              icon="done2"
              color="#22C55E"
              onClick={() =>
                navigateTo({ route: "/projects?status=delivered" })
              }
            />
            <StatCard
              label="SAV"
              value={projectsByStatus.sav}
              icon="warning"
              color="#F59E0B"
              onClick={() => navigateTo({ route: "/projects?status=sav" })}
            />
            <StatCard
              label="Artisans"
              value={partners.length}
              icon="expert"
              color="#8B5CF6"
              onClick={() => navigateTo({ route: "/partners" })}
            />
          </div>

          {/* Main content grid */}
          <div className="flex flex-row gap-4">
            {/* Recent projects */}
            <div className="flex flex-col flex-1 bg-white border rounded-xl p-4 gap-2 shadow-lg">
              <div className="flex flex-row justify-between items-center mb-2">
                <div className="font-semibold text-black">Projets récents</div>
                <button
                  onClick={() => navigateTo({ route: "/projects" })}
                  className="text-h3 text-blue-500 hover:text-blue-700"
                >
                  Voir tout
                </button>
              </div>
              <div className="flex flex-col divide-y">
                {recentProjects.map((project) => {
                  const config = statusConfig[project.status] || {
                    label: project.status,
                    type: "default" as const,
                  };
                  return (
                    <div
                      key={project.id}
                      onClick={() =>
                        navigateTo({ route: "/project/" + project.id })
                      }
                      className="flex flex-row items-center py-3 px-2 gap-3 cursor-pointer hover:bg-neutral-50 rounded-lg"
                    >
                      <div className="flex flex-col flex-1 min-w-0">
                        <div className="text-h2 font-medium text-black truncate">
                          {project.infos.general.name}
                        </div>
                        <div className="text-h3 text-neutral-400">
                          {project.infos.general.city}
                        </div>
                      </div>
                      <StatusBlock type={config.type} label={config.label} />
                    </div>
                  );
                })}
                {recentProjects.length === 0 && (
                  <div className="text-h2 text-neutral-400 py-4 text-center">
                    Aucun projet
                  </div>
                )}
              </div>
            </div>

            {/* Right column */}
            <div className="flex flex-col w-300 gap-4">
              {/* Active broadcasts */}
              <div className="flex flex-col bg-white border rounded-xl p-4 gap-2 shadow-lg">
                <div className="flex flex-row justify-between items-center mb-1">
                  <div className="font-semibold text-black">Annonces</div>
                  <button
                    onClick={() => navigateTo({ route: "/broadcasts" })}
                    className="text-h3 text-blue-500 hover:text-blue-700"
                  >
                    Voir tout
                  </button>
                </div>
                {broadcasts
                  .filter((b) => b.status === "active")
                  .slice(0, 3)
                  .map((broadcast) => (
                    <div
                      key={broadcast.id}
                      className="flex flex-col p-2 bg-neutral-50 rounded-lg gap-1"
                    >
                      <div className="text-h2 font-medium text-black">
                        {broadcast.title}
                      </div>
                      <div className="text-h3 text-neutral-400 line-clamp-2">
                        {broadcast.content.length > 80
                          ? broadcast.content.slice(0, 80) + "..."
                          : broadcast.content}
                      </div>
                    </div>
                  ))}
                {activeBroadcasts === 0 && (
                  <div className="text-h3 text-neutral-400 text-center py-2">
                    Aucune annonce active
                  </div>
                )}
              </div>

              {/* Recent emails */}
              <div className="flex flex-col bg-white border rounded-xl p-4 gap-2 shadow-lg">
                <div className="flex flex-row justify-between items-center mb-1">
                  <div className="font-semibold text-black">Emails récents</div>
                  <button
                    onClick={() => navigateTo({ route: "/emails-sent" })}
                    className="text-h3 text-blue-500 hover:text-blue-700"
                  >
                    Voir tout
                  </button>
                </div>
                {recentEmails.map((email) => (
                  <div
                    key={email.id}
                    className="flex flex-col p-2 border-l-2 border-blue-300 pl-3 gap-0.5 cursor-pointer hover:bg-neutral-50 rounded-r-lg transition-colors"
                    onClick={() => setSelectedEmailId(email.id)}
                  >
                    <div className="text-h3 font-medium text-black truncate">
                      {email.subject}
                    </div>
                    <div className="text-h4 text-neutral-400">
                      {email.emails_to?.[0] || ""} &middot;{" "}
                      {getLocalFromFlutter(email.created_at)}
                    </div>
                  </div>
                ))}
                {recentEmails.length === 0 && (
                  <div className="text-h3 text-neutral-400 text-center py-2">
                    Aucun email envoyé
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
